/**
 * The installed Meta Quest app (com.gathrxr.quest): an *immersive* PWA, i.e. a
 * Trusted Web Activity packaged with Meta's bubblewrap and
 * `horizonOSAppMode: "immersive"`. It has no visible 2D page at all: the
 * headset never composites the document (window rAF runs at 0 fps) until a
 * WebXR session exists, so the app must request `immersive-vr` itself on load.
 * Meta: https://developers.meta.com/horizon/documentation/web/pwa-webxr/
 *
 * Detection (all three):
 *  - `window.getDigitalGoodsService` exists (only inside a TWA, never in the
 *    Quest Browser or desktop Chrome tabs);
 *  - this tab was launched as the Quest app: `?pwa=quest` on the start URL, or
 *    the sessionStorage flag written on that first load (survives in-app
 *    navigations and location.replace world hops for the app's lifetime);
 *  - `navigator.xr` is present.
 *
 * sessionStorage, not localStorage, on purpose: a TWA can share storage with
 * the browser, and a future phone TWA (which also has getDigitalGoodsService
 * and navigator.xr on Chrome Android) must not inherit a Quest flag.
 *
 * Pure helpers take an optional `env` so the unit tests can drive them
 * (tests/questApp.test.ts).
 */

export const QUEST_APP_FLAG = 'gx-quest-app';
/** The start URL the app launched with (path + search). */
export const QUEST_LAUNCH_KEY = 'gx-quest-launch';
/** The last world page the app showed (path + search). */
export const QUEST_LAST_WORLD_KEY = 'gx-quest-last-world';

/**
 * GathrXR Home: a real public world (settings.vr_home = true) where VRViewer
 * draws the dashboard in 3D (components/vrhome). The immersive app's start
 * URL and the place it returns to: "Leave world", sign-in, any 2D page.
 */
export const VR_HOME_WORLD_ID = '2c14c8b4-287d-4118-a941-839e111f35fb';
export const VR_HOME_NAME = 'GathrXR Home';

/**
 * Is this world the VR Home? The id as well as the flag: saving a world from
 * the builder replaces its settings, which would drop the flag.
 */
export const isVRHomeWorld = (id: string | null | undefined, settings?: { vr_home?: unknown } | null) =>
  !!id && (id === VR_HOME_WORLD_ID || settings?.vr_home === true);

/** Where the app falls back to when it lands anywhere that isn't a world (e.g. /dashboard after a sign-in). */
export const QUEST_LOBBY_PATH = `/vr/${VR_HOME_WORLD_ID}?pwa=quest`;

export interface QuestEnv {
  search: string;
  pathname: string;
  hasDigitalGoods: boolean;
  hasXR: boolean;
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
}

const safeStorage = (): QuestEnv['storage'] => {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null; } catch { return null; }
};

export function browserQuestEnv(): QuestEnv {
  const w = typeof window !== 'undefined' ? (window as any) : undefined;
  return {
    search: w?.location?.search ?? '',
    pathname: w?.location?.pathname ?? '/',
    hasDigitalGoods: typeof w?.getDigitalGoodsService === 'function' || (!!w && 'getDigitalGoodsService' in w && w.getDigitalGoodsService !== undefined),
    hasXR: typeof navigator !== 'undefined' && !!(navigator as any).xr,
    storage: safeStorage(),
  };
}

const read = (s: QuestEnv['storage'], k: string): string | null => { try { return s?.getItem(k) ?? null; } catch { return null; } };
const write = (s: QuestEnv['storage'], k: string, v: string) => { try { s?.setItem(k, v); } catch { /* private mode */ } };

export const hasQuestParam = (search: string) => new URLSearchParams(search).get('pwa') === 'quest';

/** True inside the installed immersive Quest app (see the file comment). */
export function isQuestImmersiveApp(env: QuestEnv = browserQuestEnv()): boolean {
  if (!env.hasDigitalGoods || !env.hasXR) return false;
  return hasQuestParam(env.search) || read(env.storage, QUEST_APP_FLAG) === '1';
}

/** A world page: /vr/:id (the immersive app's only kind of page). */
export const isWorldPath = (pathname: string) => /^\/vr\/[^/]+\/?$/.test(pathname);

/**
 * Call once per page load, before anything reads isQuestImmersiveApp().
 * Writes the app flag on the launch load and remembers the launch URL and the
 * latest world page. Returns whether this is the immersive app.
 */
export function markQuestLaunch(env: QuestEnv = browserQuestEnv()): boolean {
  if (!env.hasDigitalGoods || !env.hasXR) return false;
  if (hasQuestParam(env.search)) {
    if (read(env.storage, QUEST_APP_FLAG) !== '1') write(env.storage, QUEST_LAUNCH_KEY, env.pathname + env.search);
    write(env.storage, QUEST_APP_FLAG, '1');
  }
  if (read(env.storage, QUEST_APP_FLAG) !== '1') return false;
  if (isWorldPath(env.pathname)) write(env.storage, QUEST_LAST_WORLD_KEY, env.pathname + env.search);
  return true;
}

/**
 * Where the immersive app goes when it lands on a page that isn't a world
 * (/dashboard, /welcome, /login after an auth redirect): Home, the app's
 * dashboard. (The last world shown is still remembered, QUEST_LAST_WORLD_KEY,
 * but the 2D site's rule is "everything else goes back to the dashboard".)
 */
export function questReturnPath(_env: QuestEnv = browserQuestEnv()): string {
  return QUEST_LOBBY_PATH;
}

/**
 * After sign-in inside the immersive app: never a 2D page (/dashboard,
 * /welcome) the headset can't show. A `next` that is itself a world page
 * wins; anything else goes Home.
 */
export function questNextAfterSignIn(next: string, env: QuestEnv = browserQuestEnv()): string {
  const path = next.split(/[?#]/)[0];
  return isWorldPath(path) ? next : questReturnPath(env);
}

/**
 * Session init for the early request: must match the xrStore's
 * customSessionInit in src/pages/VRViewer.tsx (createXRStore), so controllers,
 * hands and the local-floor rig behave exactly as after a normal Enter VR.
 */
export const QUEST_SESSION_INIT: XRSessionInit = {
  requiredFeatures: ['local-floor'],
  optionalFeatures: ['hand-tracking'],
};
